import { useEffect, useState } from 'react';
import { fetchMissingEventObjectRefsKPI } from '../api/missingEventObjectRefsApi';

export const useMissingEventObjectRefsKpi = (activeFile = null, options = {}) => {
  const { batchData } = options;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [batchLoadingAttempted, setBatchLoadingAttempted] = useState(false);

  const load = async (fileType) => {
    setLoading(true);
    setError(null);
    try {
      const effectiveFileType = fileType ?? activeFile ?? null;

      // Check if we have batch data available (preferred for performance)
      if (batchData && typeof batchData === 'object' && batchData.events_without_objects) {
        console.info('useMissingEventObjectRefsKpi: Using batch data for events without objects KPI');
        setData(batchData.events_without_objects);
        return;
      }

      // Fallback to individual API call if no batch data available
      console.info('useMissingEventObjectRefsKpi: Falling back to individual API call');
      const kpi = await fetchMissingEventObjectRefsKPI(effectiveFileType);
      setData(kpi);
    } catch (e) {
      setError(e?.message || 'Failed to load KPI');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);

      // If we've already attempted batch loading and failed, don't try again
      if (batchLoadingAttempted && !batchData?.events_without_objects) {
        console.warn('useMissingEventObjectRefsKpi: Batch data not available after wait, using individual call');
        try {
          const kpi = await fetchMissingEventObjectRefsKPI(activeFile ?? null);
          if (!cancelled) setData(kpi);
        } catch (e) {
          if (!cancelled) setError(e?.message || 'Failed to load KPI');
        } finally {
          if (!cancelled) setLoading(false);
        }
        return;
      }

      // First check: batch data immediately available
      if (batchData && typeof batchData === 'object' && batchData.events_without_objects) {
        console.info('🚀 useMissingEventObjectRefsKpi: IMMEDIATE - Using batch data for events without objects KPI');
        if (!cancelled) setData(batchData.events_without_objects);
        setLoading(false);
        setBatchLoadingAttempted(false);
        return;
      }

      // Wait briefly for batch data to load (3 seconds max)
      console.info('⏳ useMissingEventObjectRefsKpi: Waiting 3s for batch data to load...');
      setBatchLoadingAttempted(true);

      await new Promise(resolve => setTimeout(resolve, 3000)); // Wait 3 seconds

      if (cancelled) return;

      // Second check: batch data now available after wait?
      if (batchData && typeof batchData === 'object' && batchData.events_without_objects) {
        console.info('🚀 useMissingEventObjectRefsKpi: SUCCESS - Batch data loaded after wait');
        if (!cancelled) setData(batchData.events_without_objects);
        setLoading(false);
        setBatchLoadingAttempted(false);
        return;
      }

      // Final fallback: batch data never loaded, use individual API call
      console.warn('⚠️ useMissingEventObjectRefsKpi: Batch data still not available, falling back to individual API call');
      try {
        const kpi = await fetchMissingEventObjectRefsKPI(activeFile ?? null);
        if (!cancelled) setData(kpi);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load KPI');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [activeFile, batchData, batchLoadingAttempted]);

  return { data, loading, error, refresh: load };
};
